import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Color,
  Modal,
  Button,
  Form,
  Input,
  message,
  Select,
  Text,
  Space,
  Flex,
} from '@bb/elements-web';
import type { ISubscribeAnsweringServiceBillingProfile } from '@/app-package';
import { validateAddress } from 'api/SupraController';

type UpdateBillingContactInfoModalProps = {
  closeModal: () => void;
  handleConfirmContactInfo: () => void;
  billingInfo?: ISubscribeAnsweringServiceBillingProfile | undefined;
  setBillingInfo: Dispatch<
    SetStateAction<ISubscribeAnsweringServiceBillingProfile | undefined>
  >;
  open: boolean;
};

interface Country {
  label: string;
  value: string;
}

const CAN: Country = { label: 'CA', value: 'CA' };
const US: Country = { label: 'US', value: 'US' };
const countryOptions: Country[] = [CAN, US];

/**
 * Modal that allows a user to edit their billing contact information.
 * If a user already has a billing profile, those values will be pre-filled in the form. Otherwise, the form will be blank.
 */
const UpdateBillingContactInfoModal: React.FC<
  UpdateBillingContactInfoModalProps
> = ({
  closeModal,
  handleConfirmContactInfo,
  billingInfo,
  setBillingInfo,
  open,
}) => {
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [form] = Form.useForm<{
    email: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  }>();

  useEffect(() => {
    if (open && form) {
      form.resetFields();
      form.setFieldsValue({
        email: billingInfo?.email || '',
        address1: billingInfo?.address1 || '',
        address2: billingInfo?.address2 || '',
        city: billingInfo?.city || '',
        state: billingInfo?.state || '',
        zipCode: billingInfo?.zipCode || '',
        country: billingInfo?.country || '',
      });
    }
  }, [open, billingInfo, form]);

  const handleConfirm = async () => {
    await form.validateFields();
    const values = form.getFieldsValue();

    try {
      setIsValidatingAddress(true);

      const isValidAddress = await validateAddress({
        city: values.city,
        stateCode: values.state,
        zipCode: values.zipCode,
        countryCode: values.country,
      });

      if (!isValidAddress) {
        message.error('The provided address is not valid.');
        return;
      }

      setBillingInfo(prev => ({
        ...prev,
        email: values.email,
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        state: values.state,
        zipCode: values.zipCode,
        country: values.country,
      }));

      handleConfirmContactInfo();
    } catch (error) {
      message.error(
        'There was an error validating your address. Please try again.',
      );
    } finally {
      setIsValidatingAddress(false);
    }
  };

  return (
    <StyledModal
      dataCy="payment-confirmation-modal"
      open={open}
      title="Edit Billing Information"
      footer={false}
      onCancel={closeModal}
      width={500}
    >
      <Space size={32} direction="vertical">
        <Text type="secondary" size="large">
          Your email and billing address are required to complete the
          transaction. This will replace the current email and billing address
          on your account.
        </Text>

        <Form
          form={form}
          dataCy="payment-confirmation-contact-details-form"
          onFinish={handleConfirm}
          layout="vertical"
        >
          <StyledFormItem
            label="Email Address"
            name="email"
            rules={[{ required: true, type: 'email' }]}
            validateTrigger="onBlur"
          >
            <Input dataCy="payment-confirmation-email-input" />
          </StyledFormItem>

          <StyledFormItem
            label="Country"
            name="country"
            rules={[{ required: true }]}
            validateTrigger="onChange"
          >
            <StyledSelect
              dataCy="payment-confirmation-country-select"
              options={countryOptions}
              width={'100%'}
            />
          </StyledFormItem>

          <StyledFormItem
            label="Address 1"
            name="address1"
            rules={[{ required: true }]}
            validateTrigger="onChange"
          >
            <Input dataCy="payment-confirmation-address-1-input" />
          </StyledFormItem>

          <StyledFormItem label="Address 2 (Optional)" name="address2">
            <Input dataCy="payment-confirmation-address-2-input" />
          </StyledFormItem>

          <Flex flex={1} gap={4} vertical>
            <StyledFormItem
              label="City"
              name="city"
              rules={[{ required: true }]}
              validateTrigger="onChange"
            >
              <Input dataCy="payment-confirmation-city-input" />
            </StyledFormItem>
          </Flex>

          <StateZipWrapper>
            <Flex flex={1} gap={4} vertical>
              <StyledFormItem
                label="State / Province"
                name="state"
                rules={[{ required: true }]}
                validateTrigger="onChange"
              >
                <Input dataCy="payment-confirmation-state-input" />
              </StyledFormItem>
            </Flex>

            <Flex flex={1} gap={4} vertical>
              <StyledFormItem
                label="Zip / Postal Code"
                name="zipCode"
                rules={[{ required: true }]}
                validateTrigger="onChange"
              >
                <Input dataCy="payment-confirmation-zip-input" />
              </StyledFormItem>
            </Flex>
          </StateZipWrapper>

          <StyledButton
            size="large"
            type="primary"
            disabled={isValidatingAddress}
            loading={isValidatingAddress}
            htmlType="submit"
            dataCy="payment-confirmation-contact-details-confirm-button"
          >
            Confirm
          </StyledButton>
        </Form>
      </Space>
    </StyledModal>
  );
};

export default UpdateBillingContactInfoModal;

const StyledModal = styled(Modal)`
  &&& .ant5-modal-title {
    padding: 26px 26px 0 26px;
    margin-bottom: 32px;

    .ant5-typography {
      margin-bottom: 0;
    }
  }

  &&& .ant5-modal-body {
    padding: 0px 26px 26px 26px;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledSelect = styled(Select)`
  width: 100%;

  &&& .ant5-select-selector {
    font-size: 16px;
  }

  &&&& .ant5-select-selection-item {
    font-size: 16px;
  }
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 24px;

  &&&& label {
    font-size: 16px;
  }

  .ant5-input,
  &&& .ant5-select-selector {
    border-color: ${Color.BORDER_GREY};
    border-radius: 6px;
    font-size: 16px;
  }
`;

const StateZipWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 17px;
`;
